(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let motionObserver;

  const scrollStorageKey = window.mokaScrollStorageKey || `moka-scroll:${window.location.pathname}`;
  let scrollSaveFrame;
  const saveScrollPosition = () => {
    if (document.documentElement.classList.contains('moka-scroll-restore-pending')) return;
    try {
      sessionStorage.setItem(scrollStorageKey, String(Math.max(window.scrollY, 0)));
    } catch (error) {}
  };
  window.addEventListener('scroll', () => {
    if (scrollSaveFrame) return;
    scrollSaveFrame = window.requestAnimationFrame(() => {
      scrollSaveFrame = null;
      saveScrollPosition();
    });
  }, { passive: true });
  window.addEventListener('pagehide', saveScrollPosition);

  const restoreScrollBeforeReveal = () => {
    const savedY = Number(window.mokaPendingScrollRestore);
    if (!Number.isFinite(savedY) || savedY <= 0) return;

    const applyPosition = () => {
      const maxY = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
      const targetY = Math.min(savedY, maxY);
      window.scrollTo(0, targetY);
      return { maxY, targetY };
    };
    const reveal = () => {
      applyPosition();
      if (window.mokaScrollRestoreSafetyTimer) {
        window.clearTimeout(window.mokaScrollRestoreSafetyTimer);
        delete window.mokaScrollRestoreSafetyTimer;
      }
      document.documentElement.classList.remove('moka-scroll-restore-pending');
      delete window.mokaPendingScrollRestore;
    };

    let attempts = 0;
    let stableFrames = 0;
    let previousTarget = -1;
    const settlePosition = () => {
      const { maxY, targetY } = applyPosition();
      const isAtTarget = Math.abs(window.scrollY - targetY) <= 1;
      const targetIsStable = Math.abs(targetY - previousTarget) <= 1;

      stableFrames = isAtTarget && targetIsStable ? stableFrames + 1 : 0;
      previousTarget = targetY;
      attempts += 1;

      if ((stableFrames >= 2 && maxY >= savedY - 1) || attempts >= 18) {
        reveal();
        return;
      }
      window.requestAnimationFrame(settlePosition);
    };

    window.requestAnimationFrame(settlePosition);
  };

  const motionBlueprints = [
    {
      match: '.moka-stats',
      layers: [
        { selectors: '.moka-stats__visual, .moka-stats__content > .moka-kicker, .moka-stats__content > .moka-title, .moka-stats__content > .moka-rule', delay: 0 },
        { selectors: '.moka-stats__content > .moka-copy', delay: 180 },
        { selectors: '.moka-stats__items, .moka-stats__content > .moka-btn', delay: 360 }
      ],
      counterDelay: 860
    },
    {
      match: '.moka-factory-section',
      layers: [
        { selectors: '.moka-split__media', delay: 0 },
        { selectors: '.moka-split__content > .moka-kicker, .moka-split__content > .moka-title, .moka-split__content > .moka-rule', delay: 180 },
        { selectors: '.moka-split__content > .moka-copy, .moka-split__content > .moka-list, .moka-split__content > .moka-btn', delay: 360 }
      ]
    },
    {
      match: '[data-moka-cases]',
      layers: [
        { selectors: '.moka-heading', delay: 0 },
        { selectors: '.moka-cases__tabs', delay: 180 },
        { selectors: '.moka-cases__tabs + div', delay: 360 }
      ]
    },
    {
      match: '.moka-features-section',
      layers: [
        { selectors: '.moka-heading', delay: 0 },
        { selectors: '.moka-feature', delay: 180, stagger: 90 }
      ]
    },
    {
      match: '.moka-certificate-section',
      layers: [
        { selectors: '.moka-certificate-heading', delay: 0 },
        { selectors: '.moka-certificate-viewport, .moka-certificate-empty', delay: 220 }
      ]
    },
    {
      match: '.moka-warehouse-section',
      layers: [
        { selectors: '.moka-warehouse__content', delay: 0 },
        { selectors: '.moka-warehouse__image', delay: 220 }
      ]
    },
    {
      match: '.moka-quote',
      layers: [
        { selectors: '.moka-quote__form', delay: 0 },
        { selectors: '.moka-quote__faq', delay: 220 }
      ]
    },
    {
      contains: '.moka-partners',
      layers: [
        { selectors: '.moka-heading', delay: 0 },
        { selectors: '.moka-partner', delay: 180, stagger: 110 }
      ]
    },
    {
      contains: '.moka-category',
      layers: [
        { selectors: '.moka-heading', delay: 0 },
        { selectors: '.moka-category', delay: 180, stagger: 90 }
      ]
    }
  ];

  const formatCounterValue = (value, decimals, useGrouping) => {
    const rounded = decimals > 0 ? value.toFixed(decimals) : String(Math.round(value));
    if (!useGrouping) return rounded;
    const parts = rounded.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  const prepareCounter = (number) => {
    if (number.dataset.mokaCounterReady) return;
    const original = number.textContent.trim();
    const match = original.match(/-?\d[\d,]*(?:\.\d+)?/);
    if (!match) return;

    const numericText = match[0];
    const target = Number(numericText.replace(/,/g, ''));
    if (!Number.isFinite(target)) return;

    number.dataset.mokaCounterReady = 'true';
    number.dataset.mokaCounterOriginal = original;
    number.dataset.mokaCounterTarget = String(target);
    number.dataset.mokaCounterPrefix = original.slice(0, match.index);
    number.dataset.mokaCounterSuffix = original.slice(match.index + numericText.length);
    number.dataset.mokaCounterDecimals = String((numericText.split('.')[1] || '').length);
    number.dataset.mokaCounterGrouping = String(numericText.includes(','));
    number.setAttribute('aria-label', original);
    number.setAttribute('aria-live', 'off');

    if (!reducedMotion.matches) {
      number.textContent = `${number.dataset.mokaCounterPrefix}0${number.dataset.mokaCounterSuffix}`;
    }
  };

  const animateCounter = (number) => {
    if (number.dataset.mokaCounterAnimated) return;
    number.dataset.mokaCounterAnimated = 'true';

    const original = number.dataset.mokaCounterOriginal;
    const target = Number(number.dataset.mokaCounterTarget);
    if (!original || !Number.isFinite(target) || reducedMotion.matches) {
      if (original) number.textContent = original;
      return;
    }

    const prefix = number.dataset.mokaCounterPrefix || '';
    const suffix = number.dataset.mokaCounterSuffix || '';
    const decimals = Number(number.dataset.mokaCounterDecimals) || 0;
    const useGrouping = number.dataset.mokaCounterGrouping === 'true';
    const duration = 2000;
    const start = performance.now();

    const update = (time) => {
      const progress = Math.min((time - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      number.textContent = `${prefix}${formatCounterValue(target * eased, decimals, useGrouping)}${suffix}`;
      if (progress < 1) requestAnimationFrame(update);
      else number.textContent = original;
    };

    requestAnimationFrame(update);
  };

  const revealMotionSection = (section) => {
    if (section.dataset.mokaMotionRevealed) return;
    section.dataset.mokaMotionRevealed = 'true';
    section.classList.add('is-moka-motion-visible');
    section.querySelectorAll('.moka-motion-layer').forEach((layer) => {
      layer.classList.add('is-moka-motion-layer-visible');
    });
    const counterDelay = Number(section.dataset.mokaCounterDelay) || 0;
    window.setTimeout(() => {
      section.querySelectorAll('.moka-stat__number').forEach(animateCounter);
    }, reducedMotion.matches ? 0 : counterDelay);
    if (motionObserver) motionObserver.unobserve(section);
  };

  const getMotionObserver = () => {
    if (motionObserver || !('IntersectionObserver' in window)) return motionObserver;
    motionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) revealMotionSection(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    return motionObserver;
  };

  const initMotion = (scope) => {
    const selector = '.moka-section:not(.moka-hero)';
    const sections = [];
    if (scope instanceof Element && scope.matches(selector)) sections.push(scope);
    scope.querySelectorAll(selector).forEach((section) => sections.push(section));

    const observer = getMotionObserver();
    sections.forEach((section) => {
      if (section.dataset.mokaMotionReady) return;
      section.dataset.mokaMotionReady = 'true';
      const blueprint = motionBlueprints.find((item) => (
        (item.match && section.matches(item.match)) || (item.contains && section.querySelector(item.contains))
      ));
      const layers = blueprint ? blueprint.layers : [{ selectors: ':scope > *', delay: 0 }];
      layers.forEach((group) => {
        section.querySelectorAll(group.selectors).forEach((layer, index) => {
          layer.classList.add('moka-motion-layer');
          layer.style.setProperty('--moka-motion-delay', `${group.delay + (group.stagger || 0) * index}ms`);
        });
      });
      if (blueprint && blueprint.counterDelay) section.dataset.mokaCounterDelay = String(blueprint.counterDelay);
      section.querySelectorAll('.moka-stat__number').forEach(prepareCounter);

      if (reducedMotion.matches || !observer) revealMotionSection(section);
      else observer.observe(section);
    });

    document.documentElement.classList.add('moka-motion-ready');
  };

  const initHeaderReveal = (scope) => {
    const headers = [];
    if (scope instanceof Element && scope.matches('.moka-header')) headers.push(scope);
    scope.querySelectorAll('.moka-header').forEach((header) => headers.push(header));

    headers.forEach((header) => {
      const stickyHeader = header.closest('.moka-header-section') || header;
      if (stickyHeader.dataset.mokaScrollReady) return;
      stickyHeader.dataset.mokaScrollReady = 'true';

      let lastScrollY = Math.max(window.scrollY, 0);
      let ticking = false;

      const showHeader = () => stickyHeader.classList.remove('is-moka-header-hidden');
      const updateHeader = () => {
        const currentScrollY = Math.max(window.scrollY, 0);
        const drawerOpen = Boolean(header.querySelector('.moka-mobile-drawer.is-open'));
        const headerFocused = stickyHeader.contains(document.activeElement);

        if (currentScrollY <= 8 || drawerOpen || headerFocused || header.hasAttribute('data-moka-mega-open')) {
          showHeader();
          lastScrollY = currentScrollY;
        } else if (currentScrollY > lastScrollY + 6 && currentScrollY > header.offsetHeight) {
          stickyHeader.classList.add('is-moka-header-hidden');
          lastScrollY = currentScrollY;
        } else if (currentScrollY < lastScrollY - 2) {
          showHeader();
          lastScrollY = currentScrollY;
        }

        ticking = false;
      };

      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(updateHeader);
      }, { passive: true });
      stickyHeader.addEventListener('focusin', showHeader);
    });
  };

  const initAjaxContactForms = (scope) => {
    const selector = 'form[data-moka-ajax-contact]';
    const forms = [];
    if (scope instanceof Element && scope.matches(selector)) forms.push(scope);
    scope.querySelectorAll(selector).forEach((form) => forms.push(form));

    const showFeedback = (element, html) => {
      if (!element) return;
      if (typeof html === 'string') element.innerHTML = html;
      element.hidden = false;
      element.getBoundingClientRect();
      element.classList.add('is-visible');
      element.focus({ preventScroll: true });
    };

    const hideFeedback = (element) => {
      if (!element) return;
      element.classList.remove('is-visible');
      element.hidden = true;
    };

    const isChallengeResponse = (response, parsedDocument) => {
      const responseUrl = response.url || '';
      if (/\/challenge(?:[/?#]|$)/i.test(responseUrl)) return true;
      return Boolean(parsedDocument.querySelector(
        '.shopify-challenge__container, form[action*="/challenge"], iframe[src*="hcaptcha"], iframe[src*="recaptcha"], [data-shopify-captcha]'
      ));
    };

    const isCaptchaManaged = (form) => Boolean(
      form.dataset.hcaptchaBound ||
      form.dataset.recaptchaBound ||
      form.dataset.cptcha ||
      form.dataset.shopifyCaptcha === 'true' ||
      form.querySelector('[name="h-captcha-response"], [name="g-recaptcha-response"], [name="recaptcha-v3-token"]')
    );

    forms.forEach((form) => {
      if (form.dataset.mokaAjaxReady) return;
      form.dataset.mokaAjaxReady = 'true';

      const submitButton = form.querySelector('[data-moka-submit]');
      const success = form.querySelector('[data-moka-form-success]');
      const errors = form.querySelector('[data-moka-form-errors]');
      const originalButtonLabel = submitButton ? submitButton.textContent : '';

      const setSubmitting = (submitting) => {
        form.dataset.mokaSubmitting = String(submitting);
        form.setAttribute('aria-busy', String(submitting));
        if (!submitButton) return;
        submitButton.disabled = submitting;
        submitButton.classList.toggle('is-sending', submitting);
        submitButton.textContent = submitting
          ? (submitButton.dataset.sendingLabel || 'Sending...')
          : originalButtonLabel;
      };

      const submitNatively = () => {
        setSubmitting(false);
        form.dataset.mokaNativeSubmit = 'true';
        if (typeof form.requestSubmit === 'function') form.requestSubmit(submitButton || undefined);
        else HTMLFormElement.prototype.submit.call(form);
      };

      form.addEventListener('submit', async (event) => {
        if (form.dataset.mokaNativeSubmit === 'true') {
          delete form.dataset.mokaNativeSubmit;
          return;
        }
        if (isCaptchaManaged(form)) return;
        if (!window.fetch || !window.FormData || !window.DOMParser) return;
        if (form.dataset.mokaSubmitting === 'true') {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        hideFeedback(success);
        hideFeedback(errors);
        setSubmitting(true);

        try {
          const response = await fetch(form.action, {
            method: (form.method || 'post').toUpperCase(),
            body: new FormData(form),
            credentials: 'same-origin',
            redirect: 'follow',
            headers: { Accept: 'text/html' }
          });
          const responseHtml = await response.text();
          const parsedDocument = new DOMParser().parseFromString(responseHtml, 'text/html');

          if (isChallengeResponse(response, parsedDocument)) {
            submitNatively();
            return;
          }

          const returnedForm = parsedDocument.getElementById(form.id);
          const returnedErrors = returnedForm && returnedForm.querySelector('[data-moka-form-errors]');

          if (returnedErrors && returnedErrors.dataset.hasErrors === 'true') {
            setSubmitting(false);
            showFeedback(errors, returnedErrors.innerHTML);
            return;
          }

          if (response.ok) {
            form.reset();
            setSubmitting(false);
            showFeedback(success);
            return;
          }

          throw new Error('network');
        } catch (error) {
          setSubmitting(false);
          showFeedback(errors, '<p>We could not send your inquiry because of a network problem. Please check your connection and try again.</p>');
        }
      });
    });
  };

  const init = (scope = document) => {
    initHeaderReveal(scope);
    initAjaxContactForms(scope);
    scope.querySelectorAll('[data-moka-mobile-toggle]').forEach((button) => {
      if (button.closest('.moka-header')?.querySelector('[data-moka-mega-root]')) return;
      if (button.dataset.mokaReady) return;
      button.dataset.mokaReady = 'true';
      button.addEventListener('click', () => {
        const drawer = document.getElementById(button.getAttribute('aria-controls'));
        if (!drawer) return;
        const open = drawer.classList.toggle('is-open');
        button.setAttribute('aria-expanded', String(open));
      });
    });

    scope.querySelectorAll('[data-moka-cases]').forEach((section) => {
      if (section.dataset.mokaReady) return;
      section.dataset.mokaReady = 'true';
      const tabs = Array.from(section.querySelectorAll('[data-moka-case-tab]'));
      const panels = Array.from(section.querySelectorAll('[data-moka-case-panel]'));
      const activateTab = (tab) => {
        const target = tab.dataset.mokaCaseTab;
        tabs.forEach((item) => {
          const active = item === tab;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-selected', String(active));
        });
        panels.forEach((panel) => panel.classList.toggle('is-active', panel.dataset.mokaCasePanel === target));
      };
      tabs.forEach((tab) => {
        tab.addEventListener('mouseenter', () => activateTab(tab));
        tab.addEventListener('focus', () => activateTab(tab));
        tab.addEventListener('click', () => activateTab(tab));
      });
    });

    scope.querySelectorAll('.moka-faq').forEach((faq) => {
      if (faq.dataset.mokaAccordionReady) return;
      faq.dataset.mokaAccordionReady = 'true';
      const items = Array.from(faq.querySelectorAll('details'));

      const finishItem = (item, shouldOpen) => {
        item.open = shouldOpen;
        item.style.height = '';
        item.style.overflow = '';
        delete item.dataset.mokaFaqState;
        item.mokaFaqAnimation = null;
      };

      const animateItem = (item, shouldOpen) => {
        const summary = item.querySelector('summary');
        if (!summary) return;

        if (reducedMotion.matches || !item.animate) {
          if (item.mokaFaqAnimation) item.mokaFaqAnimation.cancel();
          finishItem(item, shouldOpen);
          return;
        }

        const wasOpen = item.open;
        const startHeight = wasOpen ? item.getBoundingClientRect().height : summary.getBoundingClientRect().height;
        if (!wasOpen) item.open = true;
        if (item.mokaFaqAnimation) item.mokaFaqAnimation.cancel();

        item.dataset.mokaFaqState = shouldOpen ? 'expanding' : 'closing';
        item.style.overflow = 'hidden';
        item.style.height = `${startHeight}px`;
        const endHeight = shouldOpen ? item.scrollHeight : summary.getBoundingClientRect().height;
        const animation = item.animate(
          { height: [`${startHeight}px`, `${endHeight}px`] },
          { duration: shouldOpen ? 420 : 360, easing: 'cubic-bezier(.22, 1, .36, 1)' }
        );
        item.mokaFaqAnimation = animation;
        animation.onfinish = () => finishItem(item, shouldOpen);
        animation.oncancel = () => {};
      };

      items.forEach((item) => {
        const summary = item.querySelector('summary');
        if (!summary) return;
        summary.addEventListener('click', (event) => {
          event.preventDefault();
          const shouldOpen = !item.open || item.dataset.mokaFaqState === 'closing';
          if (shouldOpen) {
            items.forEach((other) => {
              if (other !== item && other.open) animateItem(other, false);
            });
          }
          animateItem(item, shouldOpen);
        });
      });
    });

    scope.querySelectorAll('[data-moka-hero-slider]').forEach((slider) => {
      if (slider.dataset.mokaReady) return;
      slider.dataset.mokaReady = 'true';
      const slides = Array.from(slider.querySelectorAll('[data-moka-hero-slide]'));
      const previous = slider.querySelector('[data-moka-hero-prev]');
      const next = slider.querySelector('[data-moka-hero-next]');
      const current = slider.querySelector('[data-moka-hero-current]');
      if (slides.length < 2 || !previous || !next) return;

      let activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      const showSlide = (index) => {
        activeIndex = (index + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === activeIndex;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        if (current) current.textContent = String(activeIndex + 1);
      };

      previous.addEventListener('click', () => showSlide(activeIndex - 1));
      next.addEventListener('click', () => showSlide(activeIndex + 1));
      showSlide(activeIndex);
    });

    initMotion(scope);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  restoreScrollBeforeReveal();
})();

